/* eslint-disable camelcase */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import axios from 'axios';
import qs from 'qs';

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_BASE_API_LOCAL }),
  prepareHeaders: async (headers) => {
    const accessToken = localStorage.getItem('access');
    if (accessToken) {
      headers.set('authorization', `Bearer${accessToken}`);
    }
    return headers;
  },
  endpoints: (builder) => ({
    login: builder.mutation({
      query(body) {
        return {
          url: 'api/token/',
          method: 'POST',
          body,
        };
      },
    }),
    signup: builder.mutation({
      query(body) {
        return {
          url: 'clients/',
          method: 'POST',
          body,
        };
      },
    }),
    clientSteps: builder.mutation({
      query(body) {
        return {
          url: 'client-steps/',
          method: 'GET',
          body,
        };
      },
    }),

    verifyDocument: builder.mutation({
      query(body) {
        const queryParams = new URLSearchParams(body).toString();
        return {
          url: `verify-document/?${queryParams}`,
          method: 'GET',
        };
      },
    }),
    getContracts: builder.query({
      query: () => 'contracts/',
    }),
    createContract: builder.mutation({
      query(body) {
        return {
          url: 'create-contract/',
          method: 'POST',
          body,
          responseType: 'blob',
        };
      },
    }),
    createQrCode: builder.mutation({
      query(body) {
        return {
          url: 'create-qrcode/',
          method: 'POST',
          body,
        };
      },
    }),
  }),
});

export const getContracts = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/contracts/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const setPracticeData = async (body) => {
  const token = localStorage.getItem('access');
  const response = await axios.patch(`${process.env.REACT_APP_BASE_API_LOCAL}/practice-data/`, body, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const createClientAccessCredentials = async (body) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/client-access-credentials/`, body, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const editClientInfo = async (body) => {
  const token = localStorage.getItem('access');
  const response = await axios.patch(`${process.env.REACT_APP_BASE_API_LOCAL}/clients/${body.id}/`, body, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const editClientAccessCredentials = async (body) => {
  const token = localStorage.getItem('access');
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API_LOCAL}/client-access-credentials/${body.id}/`,
    body,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const updateClientPassword = async (body) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/update-client-password/`, body, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const updateClientPasswordAdmin = async (body) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/update-client-password-admin/`, body, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const uploadClientW9Copy = async (body) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/client-w9-copy/`, body, {
    headers: {
      Authorization: `Bearer ${token}`,
      'content-type': 'multipart/form-data',
    },
  });
  return response;
};

export const getClients = async (page = 1, pageSize = 10, search = '') => {
  const token = localStorage.getItem('access');
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  if (search && search.trim() !== '') {
    params.append('search', search.trim());
  }

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/clients/?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const resetPassword = async (body) => {
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/password-reset/`, body);
  return response;
};

export const resetPasswordComplete = async (uid, token, password) => {
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/password-reset-complete/`, {
    uid,
    token,
    new_password: password,
  });
  return response;
};

export const getClientContractFile = async (params) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/get-client-contract-file/`, {
    params,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getClientContracts = async (params) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/contracts/`, {
    params,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getClientW9Copy = async (params) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/client-w9-copy/`, {
    params,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getClientContractDetails = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/get-client-contract-details/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const signClientContract = async (params) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/sign-client-contract/${params.id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getClientSteps = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/client-steps/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getCommonMedicalPlatforms = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/common-medical-platforms/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getPracticeData = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/practice-data/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getClientAccessCredentials = async (params) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/client-access-credentials/`, {
    params,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const deleteClientAccessCredentials = async (params) => {
  const token = localStorage.getItem('access');
  const response = await axios.delete(
    `${process.env.REACT_APP_BASE_API_LOCAL}/client-access-credentials/${params.id}/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const lockClientAccount = async (params) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/lock-client-account/`, {
    params,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const unlockClientAccount = async (params) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/unlock-client-account/`, {
    params,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getSupportTickets = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/support-tickets/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getSupportTicket = async (params) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/support-tickets/${params.id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getSupportTicketMessages = async (params) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/support-tickets/`, {
    params,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const createSupportTicket = async (body) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/support-tickets/`, body, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

export const closeSupportTicket = async (body) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/close-support-ticket/`, body, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

export const createSupportTicketMessage = async (values) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/support-ticket-messages/`, values, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

export const createNewClient = async (values) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/clients/`, values, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const signup = async (values) => {
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/signup/`, values, {});
  return response;
};

export const getSupportTicketMessageFile = async (params) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/support-ticket-message-files/${params.id}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const getDashboardInfo = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/dashboard-info/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getClaimsDashboardFilterValues = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/claims-dashboard-filter-values/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getClaimsAnalysisFilterValues = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/claims-analysis-filter-values/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getBackgroundTasks = async (page = 1, pageSize = 10) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/background-tasks/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      page,
      page_size: pageSize,
    },
  });
  return response;
};

export const getBackgroundTasksStats = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/background-tasks-stats/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getBackgroundTaskOutputFileUrl = async (taskId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/background-tasks/${taskId}/download/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getClaimsAnalysisInfo = async (startDate, endDate, selectedDoctor, selectedPractice) => {
  const token = localStorage.getItem('access');

  const params = {
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    doctor: selectedDoctor || undefined,
    practice: selectedPractice || undefined,
  };

  const queryString = qs.stringify(params, { skipNulls: true });

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/claims-analysis-info/?${queryString}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response;
};

export const processClaimFile = async (file, clientId) => {
  const token = localStorage.getItem('access');

  const formData = new FormData();
  formData.append('file', file);
  formData.append('client_id', clientId);

  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/process-claims-file/`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });

  return response;
};

export const uploadClaimFile = async (file, clientId, type) => {
  const token = localStorage.getItem('access');

  const formData = new FormData();
  formData.append('file', file);
  formData.append('client_id', clientId);
  formData.append('type', type);

  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/upload-claims-file/`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });

  return response;
};

export const uploadClaimChargeSubmissionFile = async (file, clientId, onUploadProgress) => {
  const token = localStorage.getItem('access');

  const formData = new FormData();
  formData.append('file', file);
  formData.append('client_id', clientId);

  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/claims/charges-submissions/upload/`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
  });

  return response;
};

export const getClaimsDashboardInfo = async (
  startDate,
  endDate,
  selectedInsuranceType,
  selecteClaimPrimaryPayer,
  selectedDoctor,
  selectedClaimStatus
) => {
  const token = localStorage.getItem('access');

  const params = {
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    insurance_type: selectedInsuranceType || undefined,
    claim_primary_payer: selecteClaimPrimaryPayer || undefined,
    doctor: selectedDoctor || undefined,
    claim_status: selectedClaimStatus || undefined,
  };

  const queryString = qs.stringify(params, { skipNulls: true });

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/claims-dashboard-info/?${queryString}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response;
};

export const getEmailsStatus = async (startDate = null, endDate = null) => {
  const token = localStorage.getItem('access');
  const params = {};

  if (startDate) {
    params.start_date = startDate;
  }

  if (endDate) {
    params.end_date = endDate;
  }

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/emails-status/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });
  return response;
};

export const getEmailSendingLogs = async (page = 1, pageSize = 10) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/email-sending-logs/`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { page, page_size: pageSize },
  });
  return response;
};

export const sendEmail = async (formData) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/email-sending-logs/`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

export const downloadEmailAttachment = async (logId, attachmentId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/email-sending-logs/${logId}/attachments/${attachmentId}/download/`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response;
};

export const getEmailSuggestions = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/email-sending-logs/suggestions/`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response;
};

export const getComposerFromAddresses = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/email-sending-logs/from-addresses/`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response;
};

export const getComposerClients = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/email-sending-logs/clients/`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response;
};

// Employee API functions
export const getEmployees = async (
  page = 1,
  pageSize = 10,
  search = '',
  department = '',
  designation = '',
  status = '',
  startDate = '',
  endDate = '',
  sortBy = '',
  sortDirection = 'asc'
) => {
  const token = localStorage.getItem('access');
  const params = {
    page,
    page_size: pageSize,
  };

  if (search) {
    params.search = search;
  }

  if (department && department !== 'All') {
    params.department = department;
  }

  if (designation && designation !== 'All') {
    params.designation = designation;
  }

  if (status && status !== 'All') {
    params.status = status;
  }

  if (startDate) {
    params.start_date = startDate;
  }

  if (endDate) {
    params.end_date = endDate;
  }

  if (sortBy) {
    params.sort_by = sortBy;
    params.sort_direction = sortDirection;
  }

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employees/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });
  return response;
};

export const getEmployeesStats = async () => {
  const token = localStorage.getItem('access');

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employees-stats/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

// Get all employees for dropdown/form usage (no pagination)
export const getAllEmployees = async (options = {}) => {
  const token = localStorage.getItem('access');
  const params = {
    fields: 'id,employee_id,full_name',
    ...options,
  };

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employees/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });
  return response;
};

export const getEmployeeDetails = async (employeeId) => {
  const token = localStorage.getItem('access');

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employees/${employeeId}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const createEmployee = async (employeeData) => {
  const token = localStorage.getItem('access');

  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employees/`, employeeData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const updateEmployee = async (employeeId, employeeData) => {
  const token = localStorage.getItem('access');

  const response = await axios.put(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/employees/${employeeId}/`,
    employeeData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const deleteEmployee = async (employeeId) => {
  const token = localStorage.getItem('access');

  const response = await axios.delete(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employees/${employeeId}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const createEmployeeUserAccount = async (userData) => {
  const token = localStorage.getItem('access');

  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/create-employee-user-account/`,
    userData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const getEmployeeShifts = async (page = 1, pageSize = 1000) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-shifts/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      page,
      page_size: pageSize,
    },
  });
  return response;
};

export const createEmployeeShift = async (shiftData) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-shifts/`, shiftData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const updateEmployeeShift = async (shiftId, shiftData) => {
  const token = localStorage.getItem('access');
  const response = await axios.put(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-shifts/${shiftId}/`, shiftData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const deleteEmployeeShift = async (shiftId) => {
  const token = localStorage.getItem('access');
  const response = await axios.delete(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-shifts/${shiftId}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const downloadEmployeesXLSX = async (filters = {}) => {
  const token = localStorage.getItem('access');

  // Build query parameters from filters
  const params = {};
  if (filters.search) params.search = filters.search;
  if (filters.department && filters.department !== 'All') params.department = filters.department;
  if (filters.designation && filters.designation !== 'All') params.designation = filters.designation;
  if (filters.status && filters.status !== 'All') params.status = filters.status;
  if (filters.start_date) params.start_date = filters.start_date;
  if (filters.end_date) params.end_date = filters.end_date;
  if (filters.sort_by) params.sort_by = filters.sort_by;
  if (filters.sort_direction) params.sort_direction = filters.sort_direction;

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employees-download-xlsx/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
    responseType: 'blob', // Important for file downloads
  });
  return response;
};

// Expense API functions
export const getExpenses = async (
  page = 1,
  pageSize = 10,
  search = '',
  category = '',
  currency = '',
  startDate = null,
  endDate = null,
  moneyAccount = ''
) => {
  const token = localStorage.getItem('access');
  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (category && category !== 'All') params.category = category;
  if (currency && currency !== 'All') params.currency = currency;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (moneyAccount && moneyAccount !== 'All') params.money_account = moneyAccount;

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/expenses/`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return response;
};

export const getExpenseDetails = async (expenseId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/expenses/${expenseId}/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const createExpense = async (expenseData) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/expenses/`, expenseData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const updateExpense = async (expenseId, expenseData) => {
  const token = localStorage.getItem('access');
  const response = await axios.patch(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/expenses/${expenseId}/`, expenseData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const deleteExpense = async (expenseId) => {
  const token = localStorage.getItem('access');
  const response = await axios.delete(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/expenses/${expenseId}/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

// Income API functions
export const getIncomes = async (
  page = 1,
  pageSize = 10,
  search = '',
  category = '',
  currency = '',
  status = '',
  money_account = '',
  startDate = null,
  endDate = null
) => {
  const token = localStorage.getItem('access');
  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (category && category !== 'All') params.category = category;
  if (currency && currency !== 'All') params.currency = currency;
  if (status && status !== 'All') params.status = status;
  if (money_account && money_account !== 'All') params.money_account = money_account;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/income/`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return response;
};

export const getIncomeDetails = async (incomeId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/income/${incomeId}/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const createIncome = async (incomeData) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/income/`, incomeData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const updateIncome = async (incomeId, incomeData) => {
  const token = localStorage.getItem('access');
  const response = await axios.patch(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/income/${incomeId}/`, incomeData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const deleteIncome = async (incomeId) => {
  const token = localStorage.getItem('access');
  const response = await axios.delete(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/income/${incomeId}/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

// Money Account API functions
export const getMoneyAccounts = async (page = 1, pageSize = 10, search = '', accountType = '', currency = '') => {
  const token = localStorage.getItem('access');
  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (accountType && accountType !== 'All') params.account_type = accountType;
  if (currency && currency !== 'All') params.currency = currency;

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/money-accounts/`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return response;
};

export const getMoneyAccountDetails = async (accountId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/money-accounts/${accountId}/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const createMoneyAccount = async (accountData) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/money-accounts/`, accountData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const updateMoneyAccount = async (accountId, accountData) => {
  const token = localStorage.getItem('access');
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/money-accounts/${accountId}/`,
    accountData,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return response;
};

export const deleteMoneyAccount = async (accountId) => {
  const token = localStorage.getItem('access');
  const response = await axios.delete(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/money-accounts/${accountId}/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

// Payroll API functions
export const getPayrolls = async (
  page = 1,
  pageSize = 10,
  search = '',
  employeeId = '',
  month = '',
  year = '',
  paymentStatus = '',
  paymentCurrency = '',
  department = ''
) => {
  const token = localStorage.getItem('access');
  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (employeeId) params.employee_id = employeeId;
  if (month && month !== 'All') params.month = month;
  if (year && year !== 'All') params.year = year;
  if (paymentStatus && paymentStatus !== 'All') params.payment_status = paymentStatus;
  if (paymentCurrency && paymentCurrency !== 'All') params.payment_currency = paymentCurrency;
  if (department && department !== 'All') params.department = department;

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/payroll/`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return response;
};

export const getBatchEditPayrollFilters = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/filters/batch-edit-payroll/`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response;
};

export const getPayrollDetails = async (payrollId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/payroll/${payrollId}/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const createPayroll = async (payrollData) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/payroll/`, payrollData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const updatePayroll = async (payrollId, payrollData) => {
  const token = localStorage.getItem('access');
  const response = await axios.patch(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/payroll/${payrollId}/`, payrollData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const deletePayroll = async (payrollId) => {
  const token = localStorage.getItem('access');
  const response = await axios.delete(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/payroll/${payrollId}/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const getPayrollStats = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/payrolls-stats/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const batchCreatePayroll = async (payrollData) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/batch-payroll/`, payrollData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const getPayrollFile = async (params = {}) => {
  const token = localStorage.getItem('access');

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/get-payroll-file/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
    responseType: 'blob',
  });
  return response;
};

// Team Management API functions
export const getTeams = async (page = 1, pageSize = 10, search = '') => {
  const token = localStorage.getItem('access');
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  if (search && search.trim() !== '') {
    params.append('search', search.trim());
  }

  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/team-management/teams/?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const getTeamDetails = async (teamId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/team-management/teams/${teamId}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const createTeam = async (teamData) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/team-management/teams/`, teamData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const updateTeam = async (teamId, teamData) => {
  const token = localStorage.getItem('access');
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API_LOCAL}/team-management/teams/${teamId}/`,
    teamData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const deleteTeam = async (teamId) => {
  const token = localStorage.getItem('access');
  const response = await axios.delete(`${process.env.REACT_APP_BASE_API_LOCAL}/team-management/teams/${teamId}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getEmployeeTeams = async (employeeId, activeOnly = true) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/team-management/employees/${employeeId}/teams/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        active_only: activeOnly,
      },
    }
  );
  return response;
};

// Client Assignment API functions
export const getEmployeeClients = async (employeeId, activeOnly = true) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/team-management/employees/${employeeId}/clients/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        active_only: activeOnly,
      },
    }
  );
  return response;
};

export const getTeamClients = async (teamId, activeOnly = true) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/team-management/teams/${teamId}/clients/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      active_only: activeOnly,
    },
  });
  return response;
};

export const assignClient = async (employeeId, clientId, teamId, assignedById = null) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API_LOCAL}/team-management/assign-client/`,
    {
      employee_id: employeeId,
      client_id: clientId,
      team_id: teamId,
      assigned_by_id: assignedById,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const unassignClient = async (employeeId, clientId) => {
  const token = localStorage.getItem('access');
  const response = await axios.delete(
    `${process.env.REACT_APP_BASE_API_LOCAL}/team-management/unassign-client/${employeeId}/${clientId}/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

// Team Hierarchy API functions
export const getSupervisedTeams = async (supervisorId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/team-management/supervisors/${supervisorId}/teams/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const getLedTeams = async (teamLeadId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/team-management/team-leads/${teamLeadId}/teams/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const getTeamHierarchy = async (teamId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/team-management/teams/${teamId}/hierarchy/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const getEmployeeHierarchyPosition = async (employeeId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/team-management/employees/${employeeId}/hierarchy-position/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

// ==================== CLIENT ASSIGNMENT API FUNCTIONS ====================

export const getAssignedClients = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/client-assignments/assigned-clients/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getAvailableEmployeesForAssignment = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/client-assignments/available-employees/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const assignClientToEmployee = async (clientId, employeeId, assignmentId = null) => {
  const token = localStorage.getItem('access');
  const payload = {
    client_id: clientId,
    employee_id: employeeId,
  };

  // Add assignment_id if provided (for updates)
  if (assignmentId) {
    payload.assignment_id = assignmentId;
  }

  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/client-assignments/assign/`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const unassignClientFromEmployee = async (clientId) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API_LOCAL}/client-assignments/unassign/`,
    {
      client_id: clientId,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const getClientAssignmentHistory = async (clientId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/client-assignments/${clientId}/assignment-history/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

// AR Claims API functions
export const getARClaims = async (
  page = 1,
  pageSize = 10,
  searchTerm = '',
  statusFilter = 'All',
  actionFilter = 'All',
  clientFilter = 'All',
  submittedByFilter = 'All',
  startDate = null,
  endDate = null
) => {
  const token = localStorage.getItem('access');
  const params = {
    page,
    page_size: pageSize,
  };

  if (searchTerm) {
    params.search = searchTerm;
  }

  if (statusFilter && statusFilter !== 'All') {
    params.status = statusFilter;
  }

  if (actionFilter && actionFilter !== 'All') {
    params.action = actionFilter;
  }

  if (clientFilter && clientFilter !== 'All') {
    params.client = clientFilter;
  }

  if (submittedByFilter && submittedByFilter !== 'All') {
    params.submitted_by = submittedByFilter;
  }

  if (startDate) {
    params.start_date = startDate;
  }

  if (endDate) {
    params.end_date = endDate;
  }

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/ar-claims/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });
  return response;
};

export const getARClaimDetails = async (id) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/ar-claims/${id}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getARProductivityStats = async (
  clientId = null,
  submittedById = null,
  startDate = null,
  endDate = null
) => {
  const token = localStorage.getItem('access');
  const params = new URLSearchParams();

  if (clientId) {
    params.append('client', clientId);
  }
  if (submittedById) {
    params.append('submitted_by', submittedById);
  }
  if (startDate) {
    params.append('start_date', startDate);
  }
  if (endDate) {
    params.append('end_date', endDate);
  }

  const url = `${process.env.REACT_APP_BASE_API_LOCAL}/ar-productivity/${
    params.toString() ? `?${params.toString()}` : ''
  }`;

  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

// Billing Claims API functions
export const getBillingClaims = async (
  page = 1,
  pageSize = 10,
  searchTerm = '',
  statusFilter = 'All',
  clientFilter = 'All',
  submittedByFilter = 'All',
  startDate = null,
  endDate = null
) => {
  const token = localStorage.getItem('access');
  const params = {
    page,
    page_size: pageSize,
  };

  if (searchTerm) {
    params.search = searchTerm;
  }

  if (statusFilter && statusFilter !== 'All') {
    params.status = statusFilter;
  }

  if (clientFilter && clientFilter !== 'All') {
    params.client = clientFilter;
  }

  if (submittedByFilter && submittedByFilter !== 'All') {
    params.submitted_by = submittedByFilter;
  }

  if (startDate) {
    params.start_date = startDate;
  }

  if (endDate) {
    params.end_date = endDate;
  }

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/billing-claims/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });
  return response;
};

export const getBillingClaimDetails = async (id) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/billing-claims/${id}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getBillingProductivityStats = async (
  clientId = null,
  submittedById = null,
  startDate = null,
  endDate = null
) => {
  const token = localStorage.getItem('access');
  const params = new URLSearchParams();

  if (clientId) {
    params.append('client', clientId);
  }
  if (submittedById) {
    params.append('submitted_by', submittedById);
  }
  if (startDate) {
    params.append('start_date', startDate);
  }
  if (endDate) {
    params.append('end_date', endDate);
  }

  const url = `${process.env.REACT_APP_BASE_API_LOCAL}/billing-productivity/${
    params.toString() ? `?${params.toString()}` : ''
  }`;

  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

// Employee Leaves API functions
export const getEmployeeLeaves = async (
  page = 1,
  pageSize = 10,
  searchTerm = '',
  statusFilter = 'All',
  employeeFilter = 'All',
  leaveTypeFilter = 'All',
  startDate = null,
  endDate = null
) => {
  const token = localStorage.getItem('access');
  const params = {
    page,
    page_size: pageSize,
  };

  if (searchTerm) {
    params.search = searchTerm;
  }

  if (statusFilter && statusFilter !== 'All') {
    params.status = statusFilter;
  }

  if (employeeFilter && employeeFilter !== 'All') {
    params.employee = employeeFilter;
  }

  if (leaveTypeFilter && leaveTypeFilter !== 'All') {
    params.leave_type = leaveTypeFilter;
  }

  if (startDate) {
    params.start_date = startDate;
  }

  if (endDate) {
    params.end_date = endDate;
  }

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-leaves/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });
  return response;
};

export const getEmployeeLeavesFilterOptions = async () => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-leaves-filter-options/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getEmployeeLeaveDetails = async (id) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-leaves/${id}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const createEmployeeLeave = async (leaveData) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-leaves/`, leaveData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const updateEmployeeLeave = async (id, leaveData) => {
  const token = localStorage.getItem('access');
  const response = await axios.patch(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-leaves/${id}/`, leaveData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const deleteEmployeeLeave = async (id) => {
  const token = localStorage.getItem('access');
  const response = await axios.delete(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-leaves/${id}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

// Employee Leaves Settings API functions
export const getEmployeeLeavesSettings = async (page = 1, pageSize = 10, search = '', employee = 'All') => {
  const token = localStorage.getItem('access');
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  if (search) params.append('search', search);
  if (employee && employee !== 'All') params.append('employee', employee);

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-leaves-settings/?${params}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const createEmployeeLeaveSettings = async (settingsData) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-leaves-settings/`,
    settingsData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const updateEmployeeLeaveSettings = async (id, settingsData) => {
  const token = localStorage.getItem('access');
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-leaves-settings/${id}/`,
    settingsData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const deleteEmployeeLeaveSettings = async (id) => {
  const token = localStorage.getItem('access');
  const response = await axios.delete(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/employee-leaves-settings/${id}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

// Employee Attendance API functions
export const getEmployeeAttendance = async (
  page = 1,
  pageSize = 10,
  search = '',
  employee = '',
  department = '',
  status = '',
  startDate = null,
  endDate = null
) => {
  const token = localStorage.getItem('access');
  const params = new URLSearchParams();

  if (page) params.append('page', page);
  if (pageSize) params.append('page_size', pageSize);
  if (search) params.append('search', search);
  if (employee && employee !== 'All') params.append('employee', employee);
  if (department && department !== 'All') params.append('department', department);
  if (status && status !== 'All') params.append('status', status);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/attendance/?${params}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getEmployeeAttendanceDetails = async (attendanceId) => {
  const token = localStorage.getItem('access');
  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/attendance/${attendanceId}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const createEmployeeAttendance = async (attendanceData) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/attendance/`, attendanceData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const updateEmployeeAttendance = async (attendanceId, attendanceData) => {
  const token = localStorage.getItem('access');
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/attendance/${attendanceId}/`,
    attendanceData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const deleteEmployeeAttendance = async (attendanceId) => {
  const token = localStorage.getItem('access');
  const response = await axios.delete(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/attendance/${attendanceId}/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const batchCreateAttendance = async (attendanceRecords) => {
  const token = localStorage.getItem('access');
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/batch-attendance/`,
    {
      attendance_records: attendanceRecords,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const uploadAttendanceFile = async (file, shiftId, attendanceDate, markMissingAsAbsent = true) => {
  const token = localStorage.getItem('access');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('shift_id', shiftId);
  formData.append('date', attendanceDate);
  formData.append('mark_missing_as_absent', String(markMissingAsAbsent));

  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/upload-attendance-file/`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

export const generateAttendanceReport = async ({ employees = [], startDate = null, endDate = null, type = 'excel' }) => {
  const token = localStorage.getItem('access');
  const params = { type };

  if (employees.length > 0) {
    params.employees = employees.join(',');
  }
  if (startDate) {
    params.start_date = startDate;
  }
  if (endDate) {
    params.end_date = endDate;
  }

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/attendance/report`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });
  return response;
};

// Inventory API functions
export const getInventoryItems = async (
  page = 1,
  pageSize = 10,
  search = '',
  name = '',
  minQuantity = '',
  maxQuantity = '',
  addedBy = ''
) => {
  const token = localStorage.getItem('access');
  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (name && name !== 'All') params.name = name;
  if (minQuantity) params.min_quantity = minQuantity;
  if (maxQuantity) params.max_quantity = maxQuantity;
  if (addedBy && addedBy !== 'All') params.added_by = addedBy;

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-items/`, {
    params,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getInventoryItemDetails = async (itemId) => {
  const token = localStorage.getItem('access');

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-items/${itemId}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const createInventoryItem = async (itemData) => {
  const token = localStorage.getItem('access');

  const response = await axios.post(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-items/`, itemData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const updateInventoryItem = async (itemId, itemData) => {
  const token = localStorage.getItem('access');

  const response = await axios.put(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-items/${itemId}/`, itemData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const deleteInventoryItem = async (itemId) => {
  const token = localStorage.getItem('access');

  const response = await axios.delete(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-items/${itemId}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

// Inventory Assignment API functions
export const getInventoryAssignments = async (
  page = 1,
  pageSize = 10,
  search = '',
  assignedToId = '',
  inventoryItemId = '',
  returnStatus = ''
) => {
  const token = localStorage.getItem('access');
  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (assignedToId && assignedToId !== 'all') params.assigned_to_id = assignedToId;
  if (inventoryItemId && inventoryItemId !== 'all') params.inventory_item_id = inventoryItemId;
  if (returnStatus && returnStatus !== 'all') params.return_status = returnStatus;

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-assignments/`, {
    params,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getInventoryAssignmentDetails = async (assignmentId) => {
  const token = localStorage.getItem('access');

  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-assignments/${assignmentId}/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const createInventoryAssignment = async (assignmentData) => {
  const token = localStorage.getItem('access');

  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-assignments/`,
    assignmentData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const updateInventoryAssignment = async (assignmentId, assignmentData) => {
  const token = localStorage.getItem('access');

  const response = await axios.put(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-assignments/${assignmentId}/`,
    assignmentData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const deleteInventoryAssignment = async (assignmentId) => {
  const token = localStorage.getItem('access');

  const response = await axios.delete(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-assignments/${assignmentId}/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const returnInventoryAssignment = async (assignmentId, returnData = {}) => {
  const token = localStorage.getItem('access');

  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-assignments/${assignmentId}/return/`,
    returnData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

export const getInventoryAssignmentFilterOptions = async () => {
  const token = localStorage.getItem('access');

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-assignments-filter-options/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getFinanceStats = async (startDate = null, endDate = null) => {
  const token = localStorage.getItem('access');
  const params = {};

  if (startDate) {
    params.start_date = startDate;
  }
  if (endDate) {
    params.end_date = endDate;
  }

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/stats/finance-dashboard/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });
  return response;
};

export const getEmployeeDashboardFilters = async () => {
  const token = localStorage.getItem('access');

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/filters/employee-dashboard/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getEmployeeDashboardStats = async (
  employeeFilter = '',
  departmentFilter = '',
  designationFilter = '',
  statusFilter = '',
  startDate = null,
  endDate = null
) => {
  const token = localStorage.getItem('access');
  const params = {};

  if (employeeFilter && employeeFilter !== 'All') {
    params.employee_id = employeeFilter;
  }
  if (departmentFilter && departmentFilter !== 'All') {
    params.department = departmentFilter;
  }
  if (designationFilter && designationFilter !== 'All') {
    params.designation = designationFilter;
  }
  if (statusFilter && statusFilter !== 'All') {
    params.status = statusFilter;
  }
  if (startDate) {
    params.start_date = startDate;
  }
  if (endDate) {
    params.end_date = endDate;
  }

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/stats/employee-dashboard/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });
  return response;
};

export const getInventoryAssignmentStats = async () => {
  const token = localStorage.getItem('access');

  const response = await axios.get(`${process.env.REACT_APP_BASE_API_LOCAL}/hr/inventory-assignments-stats/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const {
  useLoginMutation,
  useSignupMutation,
  useNewClientMutation,
  useClientStepsMutation,
  useVerifyDocumentMutation,
  useCreateContractMutation,
  useCreateQrCodeMutation,
  useGetContractsQuery,
} = api;
